import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('rounded-2xl border border-line bg-white shadow-card', className)} {...props} />;
}

export function CardHeader({ title, description, actions, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4', className)}>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-ink">{title}</h3>
        {description && <p className="mt-0.5 text-sm text-ink-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

const badgeTones = {
  neutral: 'bg-surface-sunken text-ink-soft',
  teal: 'bg-brand-50 text-brand-800 ring-1 ring-inset ring-brand-200',
  green: 'bg-forest-50 text-forest-700 ring-1 ring-inset ring-forest-200',
  gold: 'bg-gold-50 text-gold-800 ring-1 ring-inset ring-gold-200',
  red: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  blue: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200',
  violet: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200',
} as const;

export function Badge({ tone = 'neutral', className, children }: { tone?: keyof typeof badgeTones; className?: string; children: ReactNode }) {
  return <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium [&_svg]:size-3', badgeTones[tone], className)}>{children}</span>;
}

const STATUS_TONE: Record<string, keyof typeof badgeTones> = {
  published: 'green', approved: 'green', resolved: 'green',
  draft: 'neutral', unpublished: 'neutral', dismissed: 'neutral',
  in_review: 'blue', under_review: 'blue', in_progress: 'blue', pending_review: 'gold', open: 'gold',
  scheduled: 'violet', changes_requested: 'gold',
  archived: 'neutral', rejected: 'red',
};

export function StatusBadge({ status }: { status: string }) {
  const label = status.replace(/_/g, ' ');
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'} className="capitalize">{label}</Badge>;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton rounded-xl', className)} aria-hidden />;
}

export function Spinner({ className }: { className?: string }) {
  return <span role="status" aria-label="Loading" className={cn('inline-block size-5 animate-spin rounded-full border-2 border-brand-200 border-t-brand-700', className)} />;
}

export function EmptyState({ title, description, action, icon }: { title: ReactNode; description?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line bg-surface-muted/50 px-6 py-12 text-center">
      <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-white text-brand-700 shadow-card">{icon ?? <Inbox className="size-6" />}</div>
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {description && <p className="mt-1 max-w-md text-sm text-ink-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry, retryLabel = 'Try again' }: { message: ReactNode; onRetry?: () => void; retryLabel?: string }) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center rounded-2xl border border-red-200 bg-red-50/60 px-6 py-10 text-center">
      <AlertTriangle className="mb-2 size-6 text-red-600" />
      <p className="text-sm font-medium text-red-800">{message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCw /> {retryLabel}
        </Button>
      )}
    </div>
  );
}

export function Switch({ checked, onCheckedChange, id, label, disabled }: { checked: boolean; onCheckedChange: (v: boolean) => void; id?: string; label?: ReactNode; disabled?: boolean }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2.5 text-sm">
      <SwitchPrimitive.Root id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} className="relative h-5 w-9 shrink-0 rounded-full bg-line transition data-[state=checked]:bg-brand-700 disabled:opacity-50">
        <SwitchPrimitive.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition data-[state=checked]:translate-x-[18px]" />
      </SwitchPrimitive.Root>
      {label && <span className="text-ink">{label}</span>}
    </label>
  );
}

export const Tabs = TabsPrimitive.Root;
export function TabsList({ className, ...p }: TabsPrimitive.TabsListProps) {
  return <TabsPrimitive.List className={cn('inline-flex max-w-full gap-1 overflow-x-auto rounded-xl bg-surface-sunken p-1 scrollbar-none', className)} {...p} />;
}
export function TabsTrigger({ className, ...p }: TabsPrimitive.TabsTriggerProps) {
  return <TabsPrimitive.Trigger className={cn('whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium text-ink-muted transition data-[state=active]:bg-white data-[state=active]:text-ink data-[state=active]:shadow-sm', className)} {...p} />;
}
export const TabsContent = TabsPrimitive.Content;

export function Pagination({ page, totalPages, onPage, labels = { prev: 'Previous', next: 'Next' } }: { page: number; totalPages: number; onPage: (p: number) => void; labels?: { prev: string; next: string } }) {
  if (totalPages <= 1) return null;
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1);
  return (
    <nav aria-label="Pagination" className="mt-8 flex items-center justify-center gap-1">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>{labels.prev}</Button>
      {pages.map((p, i) => (
        <span key={p} className="flex items-center">
          {i > 0 && p - pages[i - 1] > 1 && <span className="px-1 text-ink-muted">…</span>}
          <Button variant={p === page ? 'primary' : 'ghost'} size="sm" aria-current={p === page ? 'page' : undefined} onClick={() => onPage(p)}>{p}</Button>
        </span>
      ))}
      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>{labels.next}</Button>
    </nav>
  );
}
